import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { CLASSES_STORE } from '@/classes/store/classes.store';
import { _CLASSES_STORE } from '@/classes/values/classes.values';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { MY_CONTENT_STORE } from '@/app/private/my-content/store/my-content.store';

@Component({
  name: 'ed-share-course'
})
export default class EdShareCourseComponent extends Vue {

  /* Props */
  @Prop() readonly params: any;

  @Ref('dataTable') readonly dataTable: any;

  /* Data */
  public form: object = {}
  public saving: boolean = false;
  public loading: boolean = true;
  public sharingId: number = null;
  public sharing: boolean = false;
  public isCourseBlocked: boolean = false;
  public ownerRadio: boolean = true;
  public pagination: any = { current: 1, total: 0 };
  public ownerId: number = null;

  /* Computed */
  get writeClassPriv() {
    return this.$store.state.AuthStore.privileges.WriteClass;
  }

  get lockContentPriv() {
    return this.$store.state.AuthStore.privileges.LockContent;
  }

  get sharedTeachers() {
    return this.$store.state.ClassesStore.pagedSharedCourseTeachers;
  }

  get userId() {
    return this.$store.state.AuthStore.user.id;
  }

  get  headers() { 
    return [
      { text:  this.$t('classes.headers.name'), align: 'start', value: 'fullName' },
      { text: this.$t('classes.headers.email'), value: 'email' },
      { text: this.$t('classes.headers.owner'), value: 'owner' },
      { text: this.$t('classes.headers.actions'), value: 'actions', sortable: false }
    ]
  }

  get isTemplate() {
    return this.params.scope === 'template';
  }

  get teachers() {    
    this.loading = true;
    
     // Check with store if the page was loaded previously    
    let orgUsers = this.sharedTeachers?.find(el => el.page === this.pagination.current-1)?.content;

    // Fetch new page
    if (!orgUsers) {
      this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_SHARED_TEACHERS_A}`,{
        courseId: this.params.course.id, 
        page: this.pagination.current-1, 
        pageSize: 5
      }).then((res) => { 
        this.pagination.total = res.totalPages;
        this.loading = false;
        return res.content;
      }).catch(() => {
        this.loading = false;
      })
    } else this.loading = false;
    return orgUsers
  }

  get title () {
    return this.isTemplate ? this.$t('courseTemplates.templateAdmin.shareTemplate')
      : this.$t('classes.courses.shareCourse');
  }

  get subTitle() {
    return this.isTemplate ? this.$t('courseTemplates.templateAdmin.info.shareTemplateInfo', {template: this.params.course.title})
      : this.$t('classes.info.shareCourseInfo', {course: this.params.course.title})
  }

  get checkboxTooltip() {
    return this.isTemplate ? this.$t('courseTemplates.templateAdmin.info.blockTemplateInfo') 
      : this.$t('classes.info.blockCourseInfo');
  }

  get checkboxTitle() {
    return this.isTemplate ? this.$t('courseTemplates.templateAdmin.blockTemplate') 
      : this.$t('classes.courses.blockCourse');
  }

  /* Methods */
  isCourseOwner(id) {
    return id === this.$store.state.AuthStore.user.id;
  }

  closeModal() {
    this.$EdModal.closeModal();
  }

  updateOwnerConfirm(owner) {
    let params = {
      owner:  owner['firstName'], 
      courseName: this.params.course.title
    }
    let body = this.isTemplate ? this.$t('courseTemplates.templateAdmin.info.confirmChangeOwner', params)
      : this.$t('classes.courses.confirmChangeOwner', params)
    this.$EdModal.createConfirmationModal(
      {
        body, 
        action: async () => {
          await this.updateOwner(owner);
        }
      }
    );
  }

  async updateOwner(owner) {
    this.ownerRadio = null;
    try{
      await this.$store.dispatch(`${CLASSES_STORE.ACTIONS.UPDATE_COURSE_OWNER_A}`,{
        courseId: this.params.course.id, ownerId: owner.id, isTemplate: this.isTemplate
      })
      let msgSuccess = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.success.ownerUpdated')}` 
        : `${this.$t('classes.success.ownerUpdated')}`;
      this.$EdToast.create({ 
        msg:  msgSuccess,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      this.ownerRadio = true;
      this.ownerId = owner.id;
      if(this.$route.name.includes('myContent.all.subjects')) {
        let content = this.$store.state.MyContentStore.subjects.courses.content.map(el => {
          if(el.id == this.params.course.id) return {...el, authorsNames: owner.fullName, owner: owner}
          else return el
        })
        this.$store.commit(MY_CONTENT_STORE.MUTATIONS.REFRESH_CONTENT, content)
      } else if(this.$route.name.includes('myContent.all.templates')){
        this.$CourseTemplates.Store.initCourseTemplates();
      }
    } catch {
      let msgError = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.errors.ownerNoUpdated')}`
        : `${this.$t('classes.errors.ownerNoUpdated')}`;
      this.$EdToast.create({ 
        msg:  msgError,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }
  }

  async shareCourse(person) {
    const personId = person.id;
    this.sharing = true;
    this.sharingId = personId;
    try {
      this.dataTable?.updateItem(person);
      await this.$store.dispatch(`${CLASSES_STORE.ACTIONS.SHARE_COURSE_A}`,{
        courseId: this.params.course.id, 
        personId, 
        page: this.pagination.current-1,
        isTemplate: this.isTemplate
      })
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.success.coEditorAdded')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      this.sharing = false;
      this.sharingId = null;
    } catch {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.errors.coEditorNoAdded')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
      this.sharing = false;
      this.sharingId = null;
    }
  }

  async unshareCourse(personId) {
    this.sharing = true;
    try {
      await this.$store.dispatch(`${CLASSES_STORE.ACTIONS.UNSHARE_COURSE_A}`,{
        courseId: this.params.course.id, 
        personId, 
        page: this.pagination.current-1,
        isTemplate: this.isTemplate
      })
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.success.coEditorRemoved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      this.sharing = false;
      if(this.$route.name == 'myContent.all.subjects') {
        let ownsCourse = this.teachers.find(el => {
          if(el.id === this.userId && (el.access === 'CO-EDITOR' || el.access === 'OWNER')) return el
        })
        if(!ownsCourse && this.$store.state.MyContentStore.subjects.courses.content.length > 0) {
          let content = this.$store.state.MyContentStore.subjects.courses.content.filter(el => el.id !== this.params.course.id)
          this.$store.commit(MY_CONTENT_STORE.MUTATIONS.REFRESH_CONTENT, content)
          this.closeModal();
        }
      }
    } catch {
      this.$EdToast.create({ 
        msg:  `${this.$t('classes.errors.coEditorNoRemoved')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
      this.sharing = false;
    }
  }

  blockOrUnblock(checked) {
    if(checked) {
      this.blockCourse();
    } else {
      this.unblockCourse();
    }
  }

  async blockCourse(){
    try {
      this.isTemplate ? await this.$CourseTemplates.Store.blockCourseTemplate(this.params.course.id)
        : await this.$store.dispatch(`${CLASSES_STORE.ACTIONS.BLOCK_COURSE_A}`,{
          courseId: this.params.course.id
        }).then(() => {
          this.$store.commit(MY_CONTENT_STORE.MUTATIONS.BLOCK_SUBJECT_TO_SHARE, { courseId: this.params.course.id })
        });

      let msgSuccess = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.success.templateBlocked')}` 
        : `${this.$t('classes.success.courseBlocked')}`;
      this.$EdToast.create({ 
        msg:  msgSuccess,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      if(this.$route.name == 'myContent.all.subjects') {
        let content = this.$store.state.MyContentStore.subjects.courses.content.map(el => {
          if(el.id == this.params.course.id) return {...el, locked: true}
          else return el
        })
        this.$store.commit(MY_CONTENT_STORE.MUTATIONS.REFRESH_CONTENT, content)
      }
    } catch {
      let msgError = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.errors.templateNoBlocked')}` 
        : `${this.$t('classes.errors.courseNoBlocked')}`;
      this.$EdToast.create({ 
        msg: msgError,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }
  }

  async unblockCourse() {
    try {
      this.isTemplate ? this.$CourseTemplates.Store.unblockCourseTemplate(this.params.course.id)
        : await this.$store.dispatch(`${CLASSES_STORE.ACTIONS.UNBLOCK_COURSE_A}`,{
          courseId: this.params.course.id
        }).then(() => {
          this.$store.commit(MY_CONTENT_STORE.MUTATIONS.UNBLOCK_SUBJECT_TO_SHARE, { courseId: this.params.course.id });
        });
      let msgSuccess = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.success.templateUnblocked')}` 
        : `${this.$t('classes.success.courseUnblocked')}`;
      this.$EdToast.create({ 
        msg: msgSuccess,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      if(this.$route.name == 'myContent.all.subjects') {
        let content = this.$store.state.MyContentStore.subjects.courses.content.map(el => {
          if(el.id == this.params.course.id) return {...el, locked: false}
          else return el
        })
        this.$store.commit(MY_CONTENT_STORE.MUTATIONS.REFRESH_CONTENT, content)
      }
    } catch {
      let msgError = this.isTemplate ? `${this.$t('courseTemplates.templateAdmin.errors.templateNoUnblocked')}` 
        : `${this.$t('classes.errors.courseNoUnblocked')}`;
      this.$EdToast.create({ 
        msg: msgError,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }
  }

  private _loadTeachersInDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.teachers || this.teachers.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }
    
    const slots = {
      'item.owner': {
        template: document.querySelector('#course\\.owner').innerHTML,
        templateParams: [{
        }],
        expressions: [
          {
            target: '.vRadio', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isCourseOwner(params.parent.ownerId) || params.parent.writeClassPriv || params.item.access === 'OWNER',
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.vRadioFalse', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access !== 'OWNER',
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.vRadioTrue', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access === 'OWNER',
            applied: 'display: inline; cursor: inherit !important; pointer-events: none;', fallback: 'display: none;'
          },
          {
            target: '.vRadioTrue div', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isCourseBlocked,
            applied: 'opacity: 0.5;', fallback: 'opacity: 1;', concat: true,
          },
          {
            target: '.vRadioDisable', attribute: 'class', booleanAttribute: false,
            expression: (params) => params.parent.hasAccess(params.item.access, params.parent.isCourseBlocked, params.parent.ownerId, params.parent.writeClassPriv),
            applied: 'v-radio--is-disabled', fallback: 'display:none', concat: true
          },
          {
            target: 'input[type="radio"]', attribute: 'checked', booleanAttribute: true,
            expression: (params) => params.item.access === 'OWNER',
            applied: 'true', fallback: 'false',
          },
          {
            target: 'input[type="radio"]', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access !== 'OWNER' && params.parent.isCourseBlocked,
            applied: 'border: 2px solid var(--ed-color-gray-medium-light)', fallback: 'false', concat: true,
          },
        ],
        events: [
          {
            target: '.updateOwnerConfirm',
            handler: (params) => params.item.access !== 'OWNER' ? params.parent.isCourseBlocked ? null : params.parent.updateOwnerConfirm(params.item) : null
          },
        ]
      },
      'item.actions': {
        template: document.querySelector('#course\\.actions').innerHTML,
        templateParams: [{
        }],
        expressions: [
          {
            target: '.spinner', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.sharing && (params.parent.sharingId === params.item.id),
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.actionButtons', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.sharing,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.showOwner', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access === 'OWNER',
            applied: 'display: inline;', fallback: 'display: none;', concat: true
          },
          {
            target: '.showCoEditor', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access === 'CO-EDITOR',
            applied: 'display: inline;', fallback: 'display: none;', concat: true
          },
          {
            target: '.shareCourse', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access === '' && (params.parent.sharingId !== params.item.id) 
                                        && ((params.parent.isCourseOwner(params.parent.ownerId) || params.parent.writeClassPriv) && !params.parent.isCourseBlocked),
            applied: 'display: inline;', fallback: 'display: none;', concat: true
          },
          {
            target: '.unshareCourse', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.access === 'CO-EDITOR' && ((params.parent.isCourseOwner(params.parent.ownerId) || params.parent.writeClassPriv) && !params.parent.isCourseBlocked),
            applied: 'display: inline;', fallback: 'display: none;', concat: true
          },
        ],
        events: [
          { target: '.shareCourse', handler: (params) =>  params.parent.shareCourse(params.item) },
          { target: '.unshareCourse', handler: (params) =>  params.parent.unshareCourse(params.item.id) },
        ]
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.teachers,
      headers: this.headers,
      slots: slots,
    });
  }
  
  public hasAccess(access: string, isCourseBlocked: boolean, ownerId: any, writeClassPriv: any): boolean {
    const isOwner = access === 'OWNER';
    const isCourseOwnerOrHasWritePriv = this.isCourseOwner(ownerId) || writeClassPriv;
    const isCoEditor = access === 'CO-EDITOR';

    return isOwner 
        || isCourseBlocked 
        || !(!isCoEditor || (isCourseOwnerOrHasWritePriv && !isCourseBlocked))
        || !(isCoEditor || (isCourseOwnerOrHasWritePriv && !isCourseBlocked));
  }

  private _updateCourseData(newValue: boolean) {
    this.isCourseBlocked = newValue;
    this.ownerId = this.isTemplate ? this.params.course.publisher.id : this.params.course.owner.id;
    this._loadTeachersInDataTable();
  }

  public onPageUpdate(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    this.pagination['current'] = detail.page;
    this._loadTeachersInDataTable();
  }

  beforeDestroy() {
    this.$store.commit(_CLASSES_STORE.MUTATIONS.RESET_SHARED_TEACHERS_M);
  }

  @Watch('isCourseBlocked', {deep: true, immediate: true})
  updateCourseBlocked(newValue: boolean) {
      this._updateCourseData(newValue);
  }

  mounted() {
    this.isCourseBlocked = this.params.course.locked;
  }

  @Watch('params.course', {deep: true, immediate: true})
  updateCourse() {
      this._updateCourseData(this.isCourseBlocked);
  }

  @Watch('teachers', { immediate: true, deep: true })
  private _watchTeachers(): void {
    this.$nextTick(() => {
      setTimeout(() => {
        this._loadTeachersInDataTable();
      }, 500); 
    });
  }
}
